"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, Globe01 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { Button as AriaButton, Checkbox as AriaCheckbox, CheckboxGroup as AriaCheckboxGroup, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import { cx } from "@/utils/cx";
import { SUPPORTED_POOL_NETWORKS } from "../../supported-networks";
import { normalizeNetworkFilter } from "../../utils/network-filter";
import { getNetworkBadgeColor } from "../../utils/networkBadgeColor";
import { getNetworkIconPath, getNetworkTokenFallbackSymbol } from "../../utils/networkIcons";
import { getTokenIconPath } from "../../utils/tokenIcons";
import { NetworkIcon } from "../NetworkIcon";
import { FILTER_TOOLTIPS } from "./consts";
const CATALOG_NETWORKS = [...SUPPORTED_POOL_NETWORKS];
function AllNetworksIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("size-5 shrink-0 text-fg-quaternary", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Globe01 aria-hidden="true" className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/** Append-order for trigger icons so new selections appear on the right without reshuffling. */
function useAppendOrder(selected: string[]): string[] {
  const prevRef = useRef<string[]>([]);
  const selectedSet = new Set(selected);
  const kept = prevRef.current.filter(name => selectedSet.has(name));
  const added = selected.filter(name_0 => !prevRef.current.includes(name_0));
  const next = selected.length === 0 ? [] : [...kept, ...added];
  prevRef.current = next;
  return next;
}
function TriggerNetworkIcon(t0) {
  const $ = _c(7);
  const {
    network
  } = t0;
  let t1;
  if ($[0] !== network) {
    t1 = getNetworkIconPath(network);
    $[0] = network;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const customPath = t1;
  let t2;
  if ($[2] !== customPath || $[3] !== network) {
    t2 = customPath ?? getTokenIconPath(getNetworkTokenFallbackSymbol(network));
    $[2] = customPath;
    $[3] = network;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const src = t2;
  let t3;
  if ($[5] !== src) {
    t3 = <img src={src} alt="" width={20} height={20} draggable={false} className="size-5 shrink-0 object-contain" />;
    $[5] = src;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function triggerLabel(displayOrder: string[]): {
  icons: string[];
  text: string;
} {
  if (displayOrder.length === 0) {
    return {
      icons: [],
      text: "All Networks"
    };
  }
  if (displayOrder.length === 1) {
    return {
      icons: displayOrder,
      text: displayOrder[0]
    };
  }
  if (displayOrder.length === 2) {
    return {
      icons: displayOrder,
      text: displayOrder.join(", ")
    };
  }
  return {
    icons: displayOrder.slice(0, 3),
    text: `${displayOrder.length} networks`
  };
}
function NetworkRow(t0) {
  const $ = _c(16);
  const {
    name,
    onlyClassName,
    onOnly
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = t2 => {
      const {
        isSelected,
        isIndeterminate,
        isDisabled,
        isFocusVisible: isFocusVisible_0
      } = t2;
      return <><NetworkIcon network={name} size="sm" /><span className="min-w-0 flex-1 truncate"><Badge type="color" size="sm" color={getNetworkBadgeColor(name)}>{name}</Badge></span><span className="w-11 shrink-0" aria-hidden="true" /><CheckboxBase size="sm" className="relative" isSelected={isSelected} isIndeterminate={isIndeterminate} isDisabled={isDisabled} isFocusVisible={isFocusVisible_0} /></>;
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== name || $[3] !== t1) {
    t2 = <AriaCheckbox value={name} className={_temp}>{t1}</AriaCheckbox>;
    $[2] = name;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== name || $[6] !== onOnly) {
    t3 = event => {
      event.preventDefault();
      event.stopPropagation();
      onOnly(name);
    };
    $[5] = name;
    $[6] = onOnly;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onlyClassName) {
    t4 = cx("absolute top-1/2 right-9 z-10 -translate-y-1/2 rounded-md bg-secondary px-1.5 py-0.5 text-xs font-semibold text-secondary shadow-xs ring-1 ring-secondary outline-focus-ring hover:bg-primary_hover hover:text-primary focus-visible:outline-2", onlyClassName);
    $[8] = onlyClassName;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <button type="button" data-only="" onClick={t3} className={t4}>Only</button>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t5) {
    t6 = <div className="group relative flex items-center">{t2}{t5}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(t0) {
  const {
    isFocusVisible
  } = t0;
  return cx("flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 outline-hidden", "hover:bg-primary_hover", isFocusVisible && "outline-2 -outline-offset-2 outline-focus-ring");
}
export function NetworkChecklist(t0) {
  const $ = _c(18);
  const {
    value,
    onChange,
    onlyVisibility: t1
  } = t0;
  const onlyVisibility = t1 === undefined ? "always" : t1;
  const isAll = value.length === 0;
  const groupValue = isAll ? CATALOG_NETWORKS : value;
  const onlyClassName = onlyVisibility === "always" ? "opacity-100" : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 lg:focus-visible:opacity-100";
  let t2;
  if ($[0] !== onChange) {
    t2 = next => {
      onChange(normalizeNetworkFilter(next));
    };
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const commit = t2;
  const t3 = !isAll;
  let t4;
  if ($[2] !== commit) {
    t4 = checked => {
      if (checked) {
        commit([]);
      }
    };
    $[2] = commit;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isAll || $[5] !== t3 || $[6] !== t4) {
    t5 = <AriaCheckbox isSelected={isAll} isIndeterminate={t3} onChange={t4} className={_temp2}>{_temp3}</AriaCheckbox>;
    $[4] = isAll;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== commit || $[9] !== onlyClassName) {
    t6 = CATALOG_NETWORKS.map(name => <NetworkRow key={name} name={name} onlyClassName={onlyClassName} onOnly={only => commit([only])} />);
    $[8] = commit;
    $[9] = onlyClassName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== commit || $[12] !== groupValue || $[13] !== t6) {
    t7 = <AriaCheckboxGroup aria-label="Networks" value={groupValue} onChange={commit} className="flex flex-col">{t6}</AriaCheckboxGroup>;
    $[11] = commit;
    $[12] = groupValue;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex flex-col">{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp3(t0) {
  const {
    isSelected,
    isIndeterminate,
    isDisabled,
    isFocusVisible: isFocusVisible_0
  } = t0;
  return <><AllNetworksIcon /><span className="min-w-0 flex-1 truncate text-sm text-primary">All Networks</span><CheckboxBase size="sm" className="relative" isSelected={isSelected} isIndeterminate={isIndeterminate} isDisabled={isDisabled} isFocusVisible={isFocusVisible_0} /></>;
}
function _temp2(t0) {
  const {
    isFocusVisible
  } = t0;
  return cx("flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 outline-hidden", "hover:bg-primary_hover", isFocusVisible && "outline-2 -outline-offset-2 outline-focus-ring");
}
function TriggerValue(t0) {
  const $ = _c(11);
  const {
    selected
  } = t0;
  const displayOrder = useAppendOrder(selected);
  let t1;
  let t2;
  let text;
  if ($[0] !== displayOrder || $[1] !== selected.length) {
    const {
      icons,
      text: t3
    } = triggerLabel(displayOrder);
    text = t3;
    t1 = "flex min-w-0 flex-1 items-center gap-2 truncate";
    t2 = selected.length === 0 ? <AllNetworksIcon /> : icons.length > 0 ? <span className="flex shrink-0 items-center -space-x-1.5">{icons.map(_temp4)}</span> : null;
    $[0] = displayOrder;
    $[1] = selected.length;
    $[2] = t1;
    $[3] = t2;
    $[4] = text;
  } else {
    t1 = $[2];
    t2 = $[3];
    text = $[4];
  }
  let t3;
  if ($[5] !== text) {
    t3 = <span className="truncate text-sm font-normal text-primary">{text}</span>;
    $[5] = text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <span className={t1}>{t2}{t3}</span>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp4(name) {
  return <span key={name} className="inline-flex duration-200 ease-out animate-in fade-in slide-in-from-right-2 motion-reduce:animate-none"><TriggerNetworkIcon network={name} /></span>;
}
function TriggerButton(t0) {
  const $ = _c(14);
  const {
    selected,
    isOpen,
    onPress
  } = t0;
  let t1;
  if ($[0] !== isOpen) {
    t1 = t2 => {
      const {
        isFocusVisible,
        isPressed
      } = t2;
      return cx("relative flex h-11 w-full cursor-pointer items-center rounded-lg bg-primary py-2.5 pr-3.5 pl-3.5 text-left shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset", (isFocusVisible || isPressed || isOpen) && "ring-2 ring-brand", isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring");
    };
    $[0] = isOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== selected) {
    t2 = <TriggerValue selected={selected} />;
    $[2] = selected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isOpen && "rotate-180";
  let t4;
  if ($[4] !== t3) {
    t4 = cx("ml-auto size-5 shrink-0 text-fg-quaternary transition-transform duration-100", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <ChevronDown aria-hidden="true" className={t4} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isOpen || $[9] !== onPress || $[10] !== t1 || $[11] !== t2 || $[12] !== t5) {
    t6 = <AriaButton aria-expanded={isOpen} onPress={onPress} className={t1}>{t2}{t5}</AriaButton>;
    $[8] = isOpen;
    $[9] = onPress;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export function NetworkMultiSelect(t0) {
  const $ = _c(31);
  const {
    value,
    onChange,
    label: t1,
    tooltip: t2,
    variant: t3
  } = t0;
  const label = t1 === undefined ? "Network" : t1;
  const tooltip = t2 === undefined ? FILTER_TOOLTIPS.network : t2;
  const variant = t3 === undefined ? "popover" : t3;
  const [isOpen, setIsOpen] = useState(false);
  if (variant === "disclosure") {
    let t4;
    if ($[0] !== label || $[1] !== tooltip) {
      t4 = <Label tooltip={tooltip}>{label}</Label>;
      $[0] = label;
      $[1] = tooltip;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = () => setIsOpen(_temp5);
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== isOpen || $[5] !== value) {
      t6 = <TriggerButton selected={value} isOpen={isOpen} onPress={t5} />;
      $[4] = isOpen;
      $[5] = value;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== isOpen || $[8] !== onChange || $[9] !== value) {
      t7 = isOpen ? <div className="rounded-lg bg-primary py-1 shadow-xs ring-1 ring-primary ring-inset"><NetworkChecklist value={value} onChange={onChange} onlyVisibility="always" /></div> : null;
      $[7] = isOpen;
      $[8] = onChange;
      $[9] = value;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t4 || $[12] !== t6 || $[13] !== t7) {
      t8 = <div className="flex flex-col gap-1.5">{t4}{t6}{t7}</div>;
      $[11] = t4;
      $[12] = t6;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t4;
  if ($[15] !== label || $[16] !== tooltip) {
    t4 = <Label tooltip={tooltip}>{label}</Label>;
    $[15] = label;
    $[16] = tooltip;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== isOpen || $[19] !== value) {
    t5 = <TriggerButton selected={value} isOpen={isOpen} />;
    $[18] = isOpen;
    $[19] = value;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== onChange || $[22] !== value) {
    t6 = <Popover size="md" className="min-w-64"><AriaDialog aria-label="Network" className="outline-hidden"><NetworkChecklist value={value} onChange={onChange} onlyVisibility="hover" /></AriaDialog></Popover>;
    $[21] = onChange;
    $[22] = value;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== isOpen || $[25] !== t5 || $[26] !== t6) {
    t7 = <AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>{t5}{t6}</AriaDialogTrigger>;
    $[24] = isOpen;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t4 || $[29] !== t7) {
    t8 = <div className="flex flex-col gap-1.5">{t4}{t7}</div>;
    $[28] = t4;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function _temp5(open) {
  return !open;
}